import { getModeFormatDate, getYearBounds } from '@gripp/shared-logic';
import moment from 'moment';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text } from 'react-native';
import DateTimePickerModal from 'react-native-modal-datetime-picker';
import { Colors } from '../../../themes';
import { useFormContext } from '../formContext';
import { DateRangeSelectorProps } from './dateRangeSelector';

type DatePickerWheelProps = DateRangeSelectorProps & {
  onDateSelected: (...event: any[]) => void;
  isStartDate?: boolean;
  isVisible: boolean;
  onClose: () => void;
  mode?: 'date' | 'time' | 'datetime';
  name?: string;
  minDate?: string;
  pickerContainerStyleIOS?: object;
  modalStyleIOS?: object;
  confirmButtonStyleIOS?: object;
};

export const DatePickerWheelOld: React.FC<DatePickerWheelProps> = (props) => {
  const { t } = useTranslation();
  const {
    control,
    onDateSelected,
    isStartDate,
    setValue,
    isVisible,
    onClose,
    mode,
    name,
  } = props;
  const today = moment();
  const minMoment = props.minDate
    ? moment(props.minDate, 'YYYY-MM-DD', true).startOf('day')
    : null;
  const formatDate = getModeFormatDate(props.mode || 'date');

  const initialMoment =
    minMoment && minMoment.isAfter(today) ? minMoment : today;
  const [currentDate, setCurrentDate] = useState<Date>(initialMoment.toDate());
  const fieldStartKey =
    name && mode === 'time'
      ? name.replace('endTime', 'startTime')
      : 'startDate';
  const fieldEndKey =
    name && mode === 'time' ? name.replace('startTime', 'endTime') : 'endDate';
  const formContext = useFormContext();
  const getValues = formContext?.getValues || (() => ({}));

  const handleConfirm = useCallback(
    (date: Date) => {
      if (!date || (props?.minDate && moment(date).isBefore(props.minDate))) {
        return;
      }

      const newDate = moment(date);
      if (isStartDate) {
        onDateSelected(
          props.mode === 'time'
            ? newDate.toISOString()
            : newDate.format(formatDate)
        );
        setCurrentDate(newDate.toDate());
        const startDate = moment(date);
        const endDate = moment(getValues(fieldEndKey));

        if (startDate.isAfter(endDate)) setValue(fieldEndKey, '');

        onClose();
        return;
      }
      if (name?.includes('endTime') || name?.includes('endDate')) {
        const startDate = moment(getValues(fieldStartKey));

        if (startDate.isAfter(newDate)) return;
      }

      onDateSelected(
        props.mode === 'time'
          ? newDate.toISOString()
          : newDate.format(formatDate)
      );
      setCurrentDate(newDate.toDate());
      onClose();
    },
    [isStartDate, onDateSelected, control, setValue, onClose, props.mode]
  );

  const { firstDay, lastDay } = getYearBounds();
  const minDateObj = props.minDate
    ? moment(props.minDate, 'YYYY-MM-DD').startOf('day').toDate()
    : firstDay;

  return (
    <DateTimePickerModal
      isVisible={isVisible}
      mode={props.mode ?? 'date'}
      onConfirm={handleConfirm}
      customConfirmButtonIOS={({ onPress }) => (
        <Pressable
          onPress={onPress}
          style={[styles.confirmButton, props.confirmButtonStyleIOS]}
        >
          <Text style={styles.confirmLabel}>{t('alerts.confirm')}</Text>
        </Pressable>
      )}
      onCancel={onClose}
      buttonTextColorIOS={Colors.primary}
      maximumDate={lastDay}
      minimumDate={minDateObj}
      date={currentDate}
      display={'spinner'}
      negativeButton={{ textColor: Colors.primary }}
      positiveButton={{ textColor: Colors.primary }}
      key={currentDate.toISOString()}
      pickerContainerStyleIOS={[
        { justifyContent: 'center', alignItems: 'center' },
        props.pickerContainerStyleIOS,
      ]}
      modalStyleIOS={props.modalStyleIOS}
    />
  );
};

const styles = StyleSheet.create({
  confirmButton: {
    height: 57,
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderTopWidth: 0.5,
    borderTopColor: Colors.primaryGrayBackground,
  },
  confirmLabel: {
    fontSize: 20,
    fontWeight: '500',
    color: Colors.primary,
  },
});
